/** SPEC §13: eigen huisstijl per organisatie (kleuren en afgeleide varianten). */
export const BSMARTR_KLEUREN = { primair: '#0B2375', accent: '#FF7A00' } as const;

export interface Huisstijl {
  primair?: string | null;
  accent?: string | null;
  /** data-URL van het logo (alleen op de server) */
  logo?: string | null;
  logoVersie?: string | null;
}

export const isHex = (k: unknown): k is string => typeof k === 'string' && /^#[0-9a-fA-F]{6}$/.test(k);

function rgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
const hexVan = (r: number, g: number, b: number) => '#' + [r, g, b].map((x) => Math.round(Math.max(0, Math.min(255, x))).toString(16).padStart(2, '0')).join('').toUpperCase();

/** Meng kleur `a` met `b`; `deel` = aandeel van b (0–1). */
export function meng(a: string, b: string, deel: number): string {
  const [r1, g1, b1] = rgb(a);
  const [r2, g2, b2] = rgb(b);
  return hexVan(r1 + (r2 - r1) * deel, g1 + (g2 - g1) * deel, b1 + (b2 - b1) * deel);
}

function luminantie(hex: string): number {
  const [r, g, b] = rgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG-contrastverhouding (1–21). */
export function contrast(a: string, b: string): number {
  const [l1, l2] = [luminantie(a), luminantie(b)].sort((x, y) => y - x);
  return Math.round(((l1 + 0.05) / (l2 + 0.05)) * 100) / 100;
}

/** Controle bij opslaan: fout (weigeren) of waarschuwing (wel opslaan). */
export function controleerHuisstijl(h: { primair?: string | null; accent?: string | null }): { fout: string | null; waarschuwing: string | null } {
  if (h.primair != null && !isHex(h.primair)) return { fout: 'De hoofdkleur moet een hexcode zijn, bijv. #0B2375', waarschuwing: null };
  if (h.accent != null && !isHex(h.accent)) return { fout: 'De accentkleur moet een hexcode zijn, bijv. #FF7A00', waarschuwing: null };
  if (h.primair && contrast(h.primair, '#FFFFFF') < 4.5)
    return { fout: `De hoofdkleur is te licht: witte tekst is er slecht op te lezen (contrast ${contrast(h.primair, '#FFFFFF')}:1, minimaal 4,5:1). Kies een donkerdere kleur.`, waarschuwing: null };
  if (h.accent && contrast(h.accent, '#FFFFFF') < 2) return { fout: null, waarschuwing: 'De accentkleur is erg licht; knoppen met witte tekst zijn dan minder goed leesbaar.' };
  return { fout: null, waarschuwing: null };
}

/** CSS-variabelen voor de app (zie apps/web/src/styles.css :root); `donker` = donkere modus (SPEC §15). */
export function huisstijlVariabelen(h?: { primair?: string | null; accent?: string | null } | null, donker = false): Record<string, string> {
  const p = isHex(h?.primair) ? h!.primair! : BSMARTR_KLEUREN.primair;
  const a = isHex(h?.accent) ? h!.accent! : BSMARTR_KLEUREN.accent;
  const paneel = '#161D2A';
  return {
    '--accent': p,
    '--accent-2': meng(p, '#FFFFFF', 0.12),
    '--accent-bg': donker ? meng(p, paneel, 0.62) : meng(p, '#FFFFFF', 0.9),
    // tekst in de hoofdkleur moet leesbaar blijven op een donkere achtergrond
    '--accent-tekst': donker ? lichtGenoeg(p, paneel, 7) : p,
    '--merk': a,
    '--merk-2': donker ? meng(a, '#FFFFFF', 0.12) : meng(a, '#000000', 0.09),
    '--merk-bg': donker ? meng(a, paneel, 0.78) : meng(a, '#FFFFFF', 0.88),
    '--merk-licht': meng(a, '#FFFFFF', 0.45),
  };
}

/** Maakt een kleur lichter tot hij minimaal het gevraagde contrast (standaard 4,5:1) heeft met de achtergrond. */
export function lichtGenoeg(kleur: string, achtergrond: string, minimaal = 4.5): string {
  for (let d = 0; d <= 1.0001; d += 0.05) {
    const k = meng(kleur, '#FFFFFF', d);
    if (contrast(k, achtergrond) >= minimaal) return k;
  }
  return '#FFFFFF';
}
